import { useCallback, useMemo } from "react"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { resetCreatePerfilFormIdClienteState, resetCreatePerfilFormState, resetEditPerfilFormState, resetEditPerfilProdutosVinculadosState, resetProdutosVinculadosState, resetPerfisState, resetSearchPerfilNomeState, setClienteSelecionadoState, setCreatePerfilFormErrorsState, setCreatePerfilFormIdClienteState, setCreatePerfilFormNameState, setCreatePerfilFormTouchedState, setEditPerfilFormErrorsState, setEditPerfilFormIdClienteState, setEditPerfilFormIdPerfilState, setEditPerfilFormNomeState, setEditPerfilFormStatusState, setEditPerfilFormTouchedState, setEditPerfilProdutosVinculadosState, setEditPerfilVincularProdutoState, setEditPerfilVincularTipoConsumoState, setOpenCreatePerfilState, setOpenEditPerfilState, setPerfisLimitState, setPerfisPageState, setPerfisPaginationState, setPerfisState, setPerfisTotalState, setProdutosVinculadosState, setRemoveEditPerfilProdutoState, setResetClienteSelecionadoState, setSearchPerfilNomeState, validateCreatePerfilForm, validateEditPerfilForm } from "../../application/states/perfil"
import { usePerfilApi } from "../../infra/perfil"
import { useUIService } from "../ui"
import { ICliente, IProduto, ITipoUsuario } from "../../application/models"
import { useTipoConsumoService } from "../tipo-consumo"
import { ITipoConsumo } from "../../application/models/ITipoConsumo"
import { useNavigationService } from "../navigation"

export const usePerfilService = () => {
  // Dispatch
  const dispatch = useAppDispatch()
  // State
  const { currentUser } = useAppSelector((state) => state.user)
  const { 
    createPerfilForm, 
    editPerfilForm, 
    clienteSelecionado, 
    editPerfilProdutosVinculados, 
    perfilProdutosVinculados,
    perfis,
    searchPerfilNome,
    perfisPagination 
  } = useAppSelector((state) => state.perfil)
  // API
  const {
    createPerfilApi,
    updatePerfilApi,
    getPerfisByIdClienteApi,
    getProdutosVinculadosApi
  } = usePerfilApi()
  // Service
  const { setAlert, setLoading } = useUIService()
  const { navigateTo } = useNavigationService()
  const { getProdutoTipoSConsumoPadrao } = useTipoConsumoService()

  const perfisFiltrados = useMemo(() => {
    return perfis.filter((perfil) =>
      perfil.Descricao.toLowerCase().includes(searchPerfilNome.toLowerCase())
    )
  }, [perfis, searchPerfilNome])

  // Lists Actions
  const getProdutosVinculados = useCallback(async () => {
    if (currentUser) {
      if (editPerfilForm.fields.idPerfil && editPerfilForm.fields.idCliente) {
        const loadingId = Math.random().toString()

        setLoading({
          id: loadingId,
          loading: true,
          message: 'Buscando produtos e consumos...'
        })
        try {

          const findProdutosVinculados = await getProdutosVinculadosApi({
            IdCliente: editPerfilForm.fields.idCliente,
            IdPerfil: editPerfilForm.fields.idPerfil
          })

          dispatch(setProdutosVinculadosState(findProdutosVinculados))
          dispatch(setEditPerfilProdutosVinculadosState({
            produtoTipoConsumo: findProdutosVinculados
          }))

        } catch (error: any) {
          setAlert({
            message: error.response.data.error || error.response.data.message,
            type: 'error'
          })
        } finally {
          setLoading({
            id: loadingId,
            loading: false
          })
        }
      }
    }
  }, [currentUser, dispatch, editPerfilForm.fields.idCliente, editPerfilForm.fields.idPerfil, getProdutosVinculadosApi, setAlert, setLoading])

  const setEditPerfilFormIdCliente = useCallback((idCliente: number) => {
    dispatch(setEditPerfilFormIdClienteState({ idCliente }))
  }, [dispatch])

  const setPerfis = useCallback(async (idCliente: number) => {
    if (currentUser) {
      const loadingId = Math.random().toString()

      setLoading({
        id: loadingId,
        loading: true,
        message: 'Buscando perfis...'
      })
      try {
        const response = await getPerfisByIdClienteApi({
          idCliente: idCliente,
          limit: 10000,
          page: 1
        })

        dispatch(setPerfisState({
          perfis: response.perfis
        }))

        dispatch(setPerfisTotalState({
          total: response.total
        }))

      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: 'error'
        })
      } finally {
        setLoading({
          id: loadingId,
          loading: false
        })
      }
    }
  }, [currentUser, dispatch, getPerfisByIdClienteApi, setAlert, setLoading])

  // Create Perfil Form Actions
  const setOpenCreatePerfilForm = useCallback((data: {
    open: boolean
  }) => {
    dispatch(setOpenCreatePerfilState({
      open: data.open
    }))

    if (data.open) {
      navigateTo({
        route: '/dashboard/cadastro/perfil/create'
      })
    } else {
      navigateTo({
        route: '/dashboard/cadastro/perfil'
      })
    }
  }, [dispatch, navigateTo])

  const setCreatePerfilFormName = useCallback((data: {
    nome: string
  }) => {
    dispatch(setCreatePerfilFormNameState({
      nome: data.nome
    }))
  }, [dispatch])

  const setCreatePerfilFormIdCliente = useCallback((data: {
    idCliente: number
  }) => {
    dispatch(setCreatePerfilFormIdClienteState({
      idCliente: data.idCliente
    }))
  }, [dispatch])

  const resetCreatePerfilFormIdCliente = useCallback(() => {
    dispatch(resetCreatePerfilFormIdClienteState())
  }, [dispatch])

  const createPerfil = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString()

      setLoading({
        id: loadingId,
        loading: true,
        message: 'Criando perfil...'
      })
      try {
        const errors = validateCreatePerfilForm(createPerfilForm.fields)

        if (errors.length > 0) {
          dispatch(setCreatePerfilFormErrorsState({
            errors: errors
          }))
          dispatch(setCreatePerfilFormTouchedState({
            touched: true
          }))
        } else {
          await createPerfilApi({
            idCliente: createPerfilForm.fields.idCliente,
            name: createPerfilForm.fields.nome
          })

          setAlert({
            message: 'Perfil criado com sucesso!',
            type: 'success'
          })

          setOpenCreatePerfilForm({
            open: false
          })

          dispatch(resetCreatePerfilFormState())
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: 'error'
        })
      } finally {
        setLoading({
          id: loadingId,
          loading: false
        })
      }
    }
  }, [createPerfilApi, createPerfilForm.fields, currentUser, dispatch, setAlert, setLoading, setOpenCreatePerfilForm])

  // Edit Perfil Form Actions
  const setEditPerfilForm = useCallback(async (data: {
    idPerfil: number,
    nome: string,
    status: boolean
  }) => {
    if (data.idPerfil) {
      dispatch(setEditPerfilFormIdPerfilState({
        idPerfil: data.idPerfil
      }))
      dispatch(setEditPerfilFormNomeState({
        nome: data.nome
      }))
      dispatch(setEditPerfilFormStatusState({
        status: data.status
      }))
    }
  }, [dispatch])

  const setOpenEditPerfilForm = useCallback((data: {
    open: boolean
  }) => {
    dispatch(setOpenEditPerfilState({
      open: data.open
    }))

    if (data.open) {
      dispatch(resetEditPerfilProdutosVinculadosState())
      dispatch(resetProdutosVinculadosState())
      navigateTo({
        route: '/dashboard/cadastro/perfil/edit'
      })
    } else {
      dispatch(resetEditPerfilFormState())
      dispatch(resetEditPerfilProdutosVinculadosState())
      dispatch(resetProdutosVinculadosState())
      navigateTo({
        route: '/dashboard/cadastro/perfil'
      })
    }
  }, [dispatch, navigateTo])

  const setEditPerfilFormName = useCallback((data: {
    nome: string
  }) => {
    dispatch(setEditPerfilFormNomeState({
      nome: data.nome
    }))
  }, [dispatch])

  const setEditPerfilFormStatus = useCallback((data: {
    status: boolean
  }) => {
    dispatch(setEditPerfilFormStatusState({
      status: data.status
    }))
  }, [dispatch])

  const updatePerfil = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString()

      setLoading({
        id: loadingId,
        loading: true,
        message: 'Atualizando perfil...'
      })

      try {
        const errors = validateEditPerfilForm(editPerfilForm.fields)

        if (errors.length > 0) {
          dispatch(setEditPerfilFormErrorsState({
            errors: errors
          }))
          dispatch(setEditPerfilFormTouchedState({
            touched: true
          }))
        } else {
          // Filtra apenas produtos ativos e seus tipos de consumo ativos
          const produtosFiltrados = editPerfilProdutosVinculados
            .filter((produto) => produto.Produto.Ativo === true)
            .map((produto) => ({
              ...produto,
              TipoConsumo: produto.TipoConsumo.filter((tipo) => tipo.Ativo === true)
            }))

          const hasAtLeastOneTipoConsumoSelecionado = produtosFiltrados.some(
            (produto) => produto.TipoConsumo.length > 0
          )

          if (!hasAtLeastOneTipoConsumoSelecionado && editPerfilForm.fields.status === true) {
            setAlert({
              message: 'Selecione ao menos 1 Produto/Tipo de Consumo para atualizar o perfil',
              type: 'error'
            })
            dispatch(setEditPerfilFormErrorsState({
              errors: [{
                key: 'idPerfil',
                message: 'Selecione ao menos 1 Produto/Tipo de Consumo para atualizar o perfil'
              }]
            }))
            dispatch(setEditPerfilFormTouchedState({
              touched: true
            }))
            return
          }

          const payload = {
            idPerfil: editPerfilForm.fields.idPerfil,
            name: editPerfilForm.fields.nome,
            status: editPerfilForm.fields.status,
            produtosTipoConsumo: produtosFiltrados
          }

          await updatePerfilApi(payload)

          setAlert({
            message: 'Perfil atualizado com sucesso',
            type: 'success'
          })

          setOpenEditPerfilState({
            open: false
          })

          dispatch(resetEditPerfilFormState())
          dispatch(resetEditPerfilProdutosVinculadosState())

          navigateTo({
            route: '/dashboard/cadastro/perfil'
          })
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: 'error'
        })
      } finally {
        setLoading({
          id: loadingId,
          loading: false
        })
      }
    }
  }, [currentUser, setLoading, editPerfilForm.fields, dispatch, updatePerfilApi, editPerfilProdutosVinculados, setAlert, navigateTo])

  const setClienteSelecionado = useCallback((data: {
    cliente: ICliente
  }) => {
    dispatch(setClienteSelecionadoState({
      cliente: data.cliente
    }))
  }, [dispatch])

  const resetClienteSelecionado = useCallback(() => {
    dispatch(setResetClienteSelecionadoState())
  }, [dispatch])

  const getPerfisByIdCliente = useCallback(async () => {
    if (clienteSelecionado) {
      const loadingId = Math.random().toString()

      setLoading({
        id: loadingId,
        loading: true,
        message: 'Buscando perfis...'
      })

      try {
        const getPerfis = await getPerfisByIdClienteApi({
          idCliente: clienteSelecionado.IdCliente,
          limit: 10000, 
          page: 1
        })

        dispatch(setPerfisState({
          perfis: getPerfis.perfis
        }))

        dispatch(setPerfisTotalState({
          total: getPerfis.total
        }))
      } catch (error: any) {
        setAlert({
          message: error.response.data.error || error.response.data.message,
          type: 'error'
        })
      } finally {
        setLoading({
          id: loadingId,
          loading: false
        })
      }
    }
  }, [clienteSelecionado, dispatch, getPerfisByIdClienteApi, setAlert, setLoading])

  const getPerfisUsuario = useCallback(async (data: {
    idCliente: number
    limit?: number
  }) => {
    const loadingId = Math.random().toString()

    setLoading({
      id: loadingId,
      loading: true,
      message: 'Buscando perfis...'
    })

    try {
      const getPerfis = await getPerfisByIdClienteApi({
        idCliente: data.idCliente,
        limit: 10000,
        page: 1
      })

      dispatch(setPerfisState({
        perfis: getPerfis.perfis
      }))

      dispatch(setPerfisTotalState({
        total: getPerfis.total
      }))
    } catch (error: any) {
      setAlert({
        message: error.response.data.error || error.response.data.message,
        type: 'error'
      })
    } finally {
      setLoading({
        id: loadingId,
        loading: false
      })
    }
  }, [dispatch, getPerfisByIdClienteApi, setAlert, setLoading])

  const setEditPerfilVincularProduto = useCallback((data: {
    produto: IProduto
    tiposConsumo?: ITipoConsumo[]
  }) => {
    dispatch(setEditPerfilVincularProdutoState({
      produto: data.produto,
      tiposConsumo: data.tiposConsumo
    }))
  }, [dispatch])

  const setRemoveEditPerfilProduto = useCallback((data: {
    idProduto: number
  }) => {
    dispatch(setRemoveEditPerfilProdutoState({
      idProduto: data.idProduto
    }))

  }, [dispatch])

  const setEditPerfilVincularTipoConsumo = useCallback((data: {
    produto: IProduto,
    tipoConsumo: ITipoConsumo,
    ativo: boolean
  }) => {
    dispatch(setEditPerfilVincularTipoConsumoState({
      Ativo: data.ativo,
      Produto: data.produto,
      TipoConsumo: data.tipoConsumo
    }))
  }, [dispatch])

  const setPerfisPage = useCallback((data: {
    page: number
  }) => {
    dispatch(setPerfisPageState({
      page: data.page
    }))
  }, [dispatch])

  const setPerfisLimit = useCallback((data: {
    limit: number
  }) => {
    dispatch(setPerfisLimitState({
      limit: data.limit
    }))
  }, [dispatch])

  const setPerfisPagination = useCallback((data: {
    page: number
    limit: number
  }) => {
    dispatch(setPerfisPaginationState({
      limit: data.limit,
      page: data.page
    }))
  }, [dispatch])

  const resetPerfis = useCallback(() => {
    dispatch(resetPerfisState())
  }, [dispatch])

  const setSearchPerfilNome = useCallback((data: { nome: string }) => {
    dispatch(setSearchPerfilNomeState({ nome: data.nome }))
  }, [dispatch])

  const resetSearchPerfilNome = useCallback(() => {
    dispatch(resetSearchPerfilNomeState())
  }, [dispatch])

  const aplicarTiposConsumoPadraoPerfil = useCallback(async (
    hasAppliedRef: React.MutableRefObject<boolean>
  ) => {
    if (hasAppliedRef.current) return;
    if (!perfilProdutosVinculados || perfilProdutosVinculados.length === 0) return;

    const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS;
    const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO;
    const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE;
    const canEditPerfil = isDemais || isTecnologiaDaInformacao || (isCliente && editPerfilForm.fields.idCliente === currentUser?.IdCliente);

    if (!canEditPerfil) return;
    
    hasAppliedRef.current = true;

    const perfilJaTemAlgumTipoAtivo = editPerfilProdutosVinculados?.some((p) =>
      p?.TipoConsumo?.some((tc: any) => tc?.Ativo === true)
    );

    if (perfilJaTemAlgumTipoAtivo) return;

    const tiposConsumoPadrao = await getProdutoTipoSConsumoPadrao();

    perfilProdutosVinculados.forEach((produto) => {
      if (![1, 2].includes(produto.Produto.IdProduto)) return;

      setEditPerfilVincularProduto({
        produto: produto.Produto,
        tiposConsumo: produto.TipoConsumo.map((tipoConsumo) => ({
          ...tipoConsumo,
          Ativo: tiposConsumoPadrao.some(
            (padrao: any) => padrao.IdTipoConsumo === tipoConsumo.IdTipoConsumo
          )
        }))
      });
    });
  }, [perfilProdutosVinculados, currentUser, editPerfilForm.fields.idCliente, editPerfilProdutosVinculados, getProdutoTipoSConsumoPadrao, setEditPerfilVincularProduto]);

  return {
    // Lists Actions
    perfis: perfisFiltrados.slice((perfisPagination.page - 1) * perfisPagination.limit, perfisPagination.page * perfisPagination.limit),
    setPerfis,
    resetPerfis,
    // Create Perfil Form Actions
    setOpenCreatePerfilForm,
    setCreatePerfilFormName,
    setCreatePerfilFormIdCliente,
    resetCreatePerfilFormIdCliente,
    createPerfil,
    // Edit Perfil Form Actions
    setOpenEditPerfilForm,
    setEditPerfilFormIdCliente,
    setEditPerfilForm,
    setEditPerfilFormName,
    setEditPerfilFormStatus,
    // Update Perfil
    updatePerfil,
    // Cliente Selecionado
    setClienteSelecionado,
    resetClienteSelecionado,
    getPerfisByIdCliente,
    getProdutosVinculados,
    setEditPerfilVincularProduto,
    setRemoveEditPerfilProduto,
    setEditPerfilVincularTipoConsumo,
    // Pagination
    setPerfisPage,
    setPerfisLimit,
    setPerfisPagination,
    getPerfisUsuario,
    // Search
    setSearchPerfilNome,
    resetSearchPerfilNome,
    rowCount: perfisFiltrados.length,
    aplicarTiposConsumoPadraoPerfil
  }
}